import { Component, Element, Method, Prop, VNode, h } from '@stencil/core';
import { FontSize, Target } from '../types';
import { ButtonIconAppearance, ButtonIconPalette, ButtonIconSize } from './types';
import { closestElementCrossShadowBoundary, mapGet, queryElementRoots } from '../../utils';

/**
 * Passing a 'href' will render an anchor link, instead of a button. Role will
 * be set to link, or button, depending on this. It is the consumers
 * responsibility to add aria information, rel, target, for links, and any
 * button attributes for form submission.
 *
 * @slot - Adds custom content.
 */
@Component({
  shadow: true,
  styleUrl: 'button-icon.css',
  tag: 'abds-button-icon',
})
export class ButtonIcon {
  //--------------------------------------------------------------------------
  //
  //  Element
  //
  //--------------------------------------------------------------------------

  @Element() host: HTMLAbdsButtonIconElement;

  //--------------------------------------------------------------------------
  //
  //  Props
  //
  //--------------------------------------------------------------------------

  /** Sets appearance (e.g. solid-outline). */
  @Prop({ reflect: true }) appearance: ButtonIconAppearance = 'transparent';

  /** Prevents the user from interacting with the button, if passed. */
  @Prop({ reflect: true }) disabled = false;

  /** Associate button elements to forms with the same id. */
  @Prop() formId: string;

  /** Sets the URL that the hyperlink points to; Determines if component should render as anchor or button. */
  @Prop({ reflect: true }) href: string;

  /** Sets abds-icon to be rendered. */
  @Prop({ reflect: true }) icon: string;

  /** Sets aria-label and title attribute. */
  @Prop() label: string;

  /**
   * The name of the button, submitted as a pair with the button's value
   * as part of the form data, when that button is used to submit the form.
   */
  @Prop() name: string;

  /** Sets palette (e.g. brand). */
  @Prop({ reflect: true }) palette: ButtonIconPalette = 'basic';

  /**
   * The relationship of the linked `href` as space-separated link types.
   * Will only apply if `href` is passed.
   */
  @Prop() rel: string;

  /** Sets size (e.g. lg). */
  @Prop({ reflect: true }) size: ButtonIconSize = 'md';

  /**
   * Sets where to display the linked `href`.
   * Will only apply if `href` is passed.
   */
  @Prop() target: Target = '_self';

  /** Sets default behavior of the button. */
  @Prop({ reflect: true }) type: string;

  //--------------------------------------------------------------------------
  //
  //  Private Properties
  //
  //--------------------------------------------------------------------------

  /** rendered child element */
  private tagRef: HTMLAnchorElement | HTMLButtonElement;

  /** rendered child element node type */
  private Tag: 'a' | 'button' = 'button';

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  // Act on requested or nearby form based on native element `type`.
  private onClick = (event: Event): void => {
    const { tagName, type } = event.currentTarget as HTMLAnchorElement | HTMLButtonElement;

    // Set focus on click to workaround Safari issue on OX X 10.X.
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
    this.setFocus();

    if (tagName === 'BUTTON' && type !== 'button') {
      event.preventDefault();

      const { formId, host } = this;
      const targetForm: HTMLFormElement =
        queryElementRoots(host, `#${formId}`) || closestElementCrossShadowBoundary(host, 'form');

      // If form is found, submit with custom validity check.
      if (targetForm) {
        const targetFormSubmitFunction = targetForm.onsubmit as () => void;
        const eventTypeHandlers = {
          reset: (): void => {
            targetForm.reset();
          },
          submit: (): void => {
            if (targetFormSubmitFunction) targetFormSubmitFunction();
            else if (targetForm.checkValidity()) targetForm.submit();
            else targetForm.reportValidity();
          },
        };

        const eventTypeHandler = mapGet(type, eventTypeHandlers);
        typeof eventTypeHandler === 'function' && eventTypeHandler();
      }
    }
  };

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------

  /** Sets focus on child element. */
  @Method()
  async setFocus(): Promise<void> {
    this.tagRef.focus();
  }

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  componentWillRender(): void {
    this.Tag = this.href ? 'a' : 'button';
  }

  render(): VNode {
    const { Tag, disabled, onClick, href, icon, label, name, rel, target, type } = this;
    const size = {
      sm: 'md',
      md: 'xl',
      lg: '2xl',
    }[this.size] as FontSize;

    return (
      <Tag
        aria-label={label}
        class={`${this.palette} ${this.appearance}`}
        disabled={disabled}
        href={href}
        name={Tag === 'button' ? name : undefined}
        onClick={onClick}
        ref={(element: HTMLAnchorElement | HTMLButtonElement) => {
          this.tagRef = element;
        }}
        rel={Tag === 'a' && rel}
        tabIndex={disabled ? -1 : undefined}
        target={Tag === 'a' && target}
        title={label}
        type={Tag === 'button' && !type ? 'submit' : type}
      >
        {icon ? <abds-icon icon={icon} size={size} stroke-width="semibold"></abds-icon> : <slot />}
      </Tag>
    );
  }
}
